import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, HardHat, DollarSign, Receipt, CalendarDays, FileText, ArrowUpRight, ArrowDownRight, TrendingUp, ClipboardList } from 'lucide-react'
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts'
import {
  dashboardSummary, dashboardTopCustomers, dashboardMonthlyCombined, dashboardQuickStats,
  type DashboardSummary, type TopCustomerRow, type MonthlyCombinedRow, type QuickStats,
} from '../services/dashboard'
import { PageHeader, Card, Spinner } from '../components/shared/ui'
import { currency, number } from '../lib/format'

// ── Date range presets (half-open [start, end)) ────────────────────────────
type Preset = 'today' | 'week' | 'month' | 'ytd' | 'lastyear' | 'custom'

const PRESETS: { key: Preset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'ytd', label: 'This Year (YTD)' },
  { key: 'lastyear', label: 'Last Year' },
  { key: 'custom', label: 'Custom' },
]

function startOfDay(d: Date) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
function addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x }
function shiftYear(d: Date, n: number) { const x = new Date(d); x.setFullYear(x.getFullYear() + n); return x }

function rangeFor(preset: Preset, customStart: string, customEnd: string): { start: Date; end: Date } {
  const now = new Date()
  const today = startOfDay(now)
  switch (preset) {
    case 'today': return { start: today, end: addDays(today, 1) }
    case 'week': {
      const day = today.getDay()
      const mon = addDays(today, -(day === 0 ? 6 : day - 1))
      return { start: mon, end: addDays(mon, 7) }
    }
    case 'month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1)
      return { start: first, end: new Date(today.getFullYear(), today.getMonth() + 1, 1) }
    }
    case 'ytd': {
      const first = new Date(today.getFullYear(), 0, 1)
      return { start: first, end: addDays(today, 1) }
    }
    case 'lastyear': {
      const first = new Date(today.getFullYear() - 1, 0, 1)
      return { start: first, end: new Date(today.getFullYear(), 0, 1) }
    }
    case 'custom': {
      const s = customStart ? startOfDay(new Date(customStart)) : new Date(today.getFullYear(), 0, 1)
      const e = customEnd ? addDays(startOfDay(new Date(customEnd)), 1) : addDays(today, 1)
      return { start: s, end: e }
    }
  }
}

function pctDelta(curr: number, prev: number): number | null {
  if (!prev) return null
  return ((curr - prev) / prev) * 100
}

// KPI card with a value + optional period-over-period delta vs the same range
// one year earlier.
function KpiCard({ label, value, prev, icon, accent }: {
  label: string; value: number; prev?: number; icon: React.ReactNode; accent: string
}) {
  const accents: Record<string, string> = {
    green: 'bg-green-100 text-green-700', blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700', purple: 'bg-purple-100 text-purple-700',
  }
  const delta = prev != null ? pctDelta(value, prev) : null
  const up = (delta ?? 0) >= 0
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{currency(value)}</p>
          {delta != null && (
            <p className={`text-xs mt-1 flex items-center gap-0.5 ${up ? 'text-green-600' : 'text-red-600'}`}>
              {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
              {Math.abs(delta).toFixed(1)}% vs last year
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${accents[accent] ?? accents.green}`}>{icon}</div>
      </div>
    </Card>
  )
}

function QuickStat({ label, value, icon, onClick }: { label: string; value: number; icon: React.ReactNode; onClick?: () => void }) {
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white text-left w-full ${onClick ? 'hover:border-green-400 hover:shadow-sm' : ''}`}>
      <div className="p-2.5 rounded-lg bg-gray-100 text-gray-600">{icon}</div>
      <div>
        <p className="text-xl font-bold text-gray-900 leading-tight">{number(value)}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </button>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [preset, setPreset] = useState<Preset>('ytd')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [prevSummary, setPrevSummary] = useState<DashboardSummary | null>(null)
  const [topCustomers, setTopCustomers] = useState<TopCustomerRow[]>([])
  const [monthly, setMonthly] = useState<MonthlyCombinedRow[]>([])
  const [quick, setQuick] = useState<QuickStats | null>(null)
  const [loading, setLoading] = useState(true)

  const range = useMemo(() => rangeFor(preset, customStart, customEnd), [preset, customStart, customEnd])

  // Quick stats are range-independent; load once.
  useEffect(() => { dashboardQuickStats().then(setQuick).catch(() => setQuick(null)) }, [])

  useEffect(() => {
    let active = true
    setLoading(true)
    const startIso = range.start.toISOString()
    const endIso = range.end.toISOString()
    const prevStart = shiftYear(range.start, -1).toISOString()
    const prevEnd = shiftYear(range.end, -1).toISOString()
    Promise.all([
      dashboardSummary(startIso, endIso),
      dashboardSummary(prevStart, prevEnd).catch(() => null),
      dashboardTopCustomers(startIso, endIso, 10).catch(() => []),
      dashboardMonthlyCombined(startIso, endIso).catch(() => []),
    ]).then(([s, ps, tc, mc]) => {
      if (!active) return
      setSummary(s); setPrevSummary(ps); setTopCustomers(tc); setMonthly(mc)
    }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [range])

  const totalSales = (summary?.pos_sales ?? 0) + (summary?.contractor_sales ?? 0)
  const prevTotalSales = (prevSummary?.pos_sales ?? 0) + (prevSummary?.contractor_sales ?? 0)
  const totalTxns = (summary?.pos_count ?? 0) + (summary?.contractor_count ?? 0)

  const chartData = monthly.map(r => ({
    month: new Date(r.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    POS: r.pos_sales,
    Contractor: r.contractor_sales,
  }))

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Sales & business overview" />

      {/* Quick data — appointments today / this week, open work */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <QuickStat label="Appointments today" value={quick?.appointmentsToday ?? 0} icon={<CalendarDays size={18} />} onClick={() => navigate('/appointments')} />
        <QuickStat label="Appointments this week" value={quick?.appointmentsThisWeek ?? 0} icon={<CalendarDays size={18} />} onClick={() => navigate('/appointments')} />
        <QuickStat label="Open estimates" value={quick?.openEstimates ?? 0} icon={<FileText size={18} />} onClick={() => navigate('/estimates')} />
        <QuickStat label="Bills w/ balance" value={quick?.openBills ?? 0} icon={<ClipboardList size={18} />} onClick={() => navigate('/bills')} />
      </div>

      {/* Date range filter */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {PRESETS.map(p => (
          <button key={p.key} onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              preset === p.key ? 'bg-green-700 text-white border-green-700'
                : 'bg-white text-gray-600 border-gray-300 hover:border-green-600 hover:text-green-700'}`}>
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2 ml-1">
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm" />
          </div>
        )}
      </div>

      {loading || !summary ? <Spinner label="Loading dashboard…" /> : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Total Sales" value={totalSales} prev={prevTotalSales} icon={<DollarSign size={20} />} accent="green" />
            <KpiCard label="POS Sales (retail)" value={summary.pos_sales} prev={prevSummary?.pos_sales} icon={<ShoppingCart size={20} />} accent="blue" />
            <KpiCard label="Contractor Sales" value={summary.contractor_sales} prev={prevSummary?.contractor_sales} icon={<HardHat size={20} />} accent="amber" />
            <Card className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500">Transactions</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{number(totalTxns)}</p>
                  <p className="text-xs text-gray-500 mt-1">{number(summary.pos_count)} POS · {number(summary.contractor_count)} jobs</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-100 text-purple-700"><Receipt size={20} /></div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Monthly POS vs Contractor */}
            <Card className="p-5 lg:col-span-2">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><TrendingUp size={16} /> Sales by Month — POS vs Contractor</h3>
              {chartData.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => currency(v)} />
                    <Legend />
                    <Bar dataKey="POS" stackId="s" fill="#0ea5e9" />
                    <Bar dataKey="Contractor" stackId="s" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-center py-10">No sales in this period</p>}
            </Card>

            {/* Top 10 customers */}
            <Card className="p-5">
              <h3 className="font-semibold text-gray-800 mb-4">Top 10 Customers</h3>
              {topCustomers.length ? (
                <div className="space-y-1">
                  {topCustomers.map((c, i) => (
                    <button key={c.customer_id ?? i}
                      onClick={() => c.customer_id && navigate(`/customers/${c.customer_id}`)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 text-left">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-gray-400 w-4 text-right">{i + 1}</span>
                        <span className="text-sm text-gray-800 truncate">{c.customer_name}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{currency(c.total_sales)}</span>
                    </button>
                  ))}
                </div>
              ) : <p className="text-gray-400 text-center py-10">No customer sales in this period</p>}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
